import { Immutable } from "immer";
import React from "react";
import { StoreSearchFilters } from "tsi-common-react/src/apps/retail/models";
import { RetailStore } from "tsi-common-react/src/models/location";
import { unique } from "tsi-common-react/src/utils/functional";
import { t } from "ttag";

import { LocatorFilterPopover } from "../LocatorFilters_Popover";
import { FilterChangeHandler } from "../models";

const RETAILERS_DIALOG_TITLE_ID = "retailers-filter-dialog-title";

type IProps = Immutable<{
    filterValues: StoreSearchFilters;
    stores: ReadonlyArray<RetailStore>;
    onChange: FilterChangeHandler;
}>;

const getStoreNames = (stores: Immutable<RetailStore[]>): string[] => {
    return stores
        .map((s) => s.name)
        .filter(unique)
        .sort();
};

const buildInputs = (
    storeNames: string[],
    selectedStoreNames: Set<string>,
    onInputChange: (event: React.FormEvent<HTMLInputElement>) => void,
): React.ReactNode[] => {
    return storeNames.map((name) => {
        return (
            <label key={name} className="filters__list-item">
                <input
                    type="checkbox"
                    name="retailer"
                    value={name}
                    onChange={onInputChange}
                    checked={selectedStoreNames.has(name)}
                />{" "}
                {name}
            </label>
        );
    });
};

export const RetailerFilter = (props: IProps): React.ReactNode => {
    const [selectedStoreNames, setSelectedStoreNames] = React.useState<
        Set<string>
    >(new Set(props.filterValues.retailerName));

    const filterElem = React.useRef<{
        onRequestClose: () => void;
    }>(null);

    // Handle getDerivedStateFromProps logic: sync selectedStoreNames with Redux filter values
    const prevFilterValueRef = React.useRef<readonly string[]>(
        props.filterValues.retailerName,
    );
    React.useEffect(() => {
        if (props.filterValues.retailerName !== prevFilterValueRef.current) {
            prevFilterValueRef.current = props.filterValues.retailerName;
            setSelectedStoreNames(new Set(props.filterValues.retailerName));
        }
    }, [props.filterValues.retailerName]);

    const onInputChange = (event: React.FormEvent<HTMLInputElement>) => {
        const checked = event.currentTarget.checked;
        const value = event.currentTarget.value;
        setSelectedStoreNames((prev) => {
            const updated = new Set(prev);
            if (checked) {
                updated.add(value);
            } else {
                updated.delete(value);
            }
            return updated;
        });
    };

    const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        props.onChange({
            retailerName: Array.from(selectedStoreNames),
        });
        if (filterElem.current) {
            filterElem.current.onRequestClose();
        }
    };

    const onReset = () => {
        // Deselect everything
        props.onChange({
            retailerName: [],
        });
        if (filterElem.current) {
            filterElem.current.onRequestClose();
        }
    };

    const onCancel = () => {
        // Close the modal
        if (filterElem.current) {
            filterElem.current.onRequestClose();
        }
    };

    const onAfterClose = () => {
        // Revert filter back to the state in Redux
        setSelectedStoreNames(new Set(prevFilterValueRef.current));
    };

    const storeNames = getStoreNames(props.stores);

    const inputs = buildInputs(storeNames, selectedStoreNames, onInputChange);

    return (
        <LocatorFilterPopover
            ref={filterElem}
            id="retailers"
            labelText={t`retailers`}
            dialogLabelledById={RETAILERS_DIALOG_TITLE_ID}
            onAfterClose={onAfterClose}
        >
            <form
                className="filters__dropdown filters__dropdown--retailers"
                onSubmit={onSubmit}
            >
                <fieldset>
                    <legend
                        className="filters__instruct"
                        id={RETAILERS_DIALOG_TITLE_ID}
                    >
                        {t`Narrow your search results by selecting the retailers that interest you.`}
                    </legend>
                    <h3 className="filters__header">
                        {t`Retailers `}
                        <span className="filters__header--right">{t`A-Z`}</span>
                    </h3>
                    <div className="filters__list filters__list--scrollable bh-sl-filters">
                        {inputs}
                    </div>
                    <div className="filters__actions filters__actions--full-width">
                        <label
                            htmlFor="retailer-apply-button"
                            style={{ display: "none" }}
                        >
                            {t`Apply Selections`}
                        </label>
                        <input
                            id="retailer-apply-button"
                            className="filters__button button"
                            type="submit"
                            value={t`Apply Selections`}
                        />
                        <p className="filters__links">
                            <button
                                type="button"
                                className="filters__link close-reset"
                                onClick={onReset}
                            >
                                {t`Reset`}
                            </button>
                            <button
                                type="button"
                                className="filters__link close-tooltip"
                                onClick={onCancel}
                            >
                                {t`Cancel`}
                            </button>
                        </p>
                    </div>
                </fieldset>
            </form>
        </LocatorFilterPopover>
    );
};
